import React, { useCallback, useContext } from "react";
import { createUseStyles } from "react-jss";
import PropTypes from "prop-types";
import _ from "lodash";
import { InlineIcon } from "@iconify/react";

import { StyleTypeContext, TranslationContext } from "../../umbilical_tools";
import jssVariables from "../../jss_setup";
import { Button } from "../../components/common/Button";
import { DateTime } from "luxon";
import tinycolor from "tinycolor2";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { gotoGuestInfo, initGuestInformation } from "../../actions/book";
import { resetPriceInfo } from "../../actions/search";
import { getPriceDecimalsSelector } from "../../selectors";
import { getRoomMappingInfoSelector } from "../../selectors/booking_process/static_packages";

const styles = createUseStyles({
  Deposit: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    ..._.get(props, "customStyles.Hotels", {}),
  }),
  content: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    justifySelf: "center",
    borderRadius: jssVariables.border_radius,
    background: "white",
    maxHeight: "85vh",
    overflowY: "auto",
    paddingTop: jssVariables.normal_gap,

    boxShadow: jssVariables.shadows.normalShadow,
    "& > *:not(:last-child)": {
      padding: [0, "4vw", 0, "4vw"],
    },
    ..._.get(props, "customStyles.content", {}),
  }),
  paymentTitle: (props) => ({
    fontSize: "x-large",
    fontWeight: "500",
    color: jssVariables.colors.base_color,
    ..._.get(props, "customStyles.paymentTitle", {}),
  }),
  paymentDescription: (props) => ({
    fontSize: "medium",
    ..._.get(props, "customStyles.paymentDescription", {}),
  }),
  btnsCntr: (props) => ({
    display: "grid",
    gridTemplateColumns: "repeat(2,1fr)",
    gridGap: jssVariables.normal_gap,
    ..._.get(props, "customStyles.btnsCntr", {}),
  }),
  detailsCntr: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    fontSize: "large",
    fontWeight: "400",
    ..._.get(props, "customStyles.detailsCntr", {}),
  }),
  detailsTotalPrice: (props) => ({
    fontWeight: "500",
    ..._.get(props, "customStyles.totalPrice", {}),
  }),
  pricesCntr: (props) => ({
    display: "grid",
    gridGap: jssVariables.half_gap,
    fontSize: "medium",
    fontWeight: "400",
    ..._.get(props, "customStyles.pricesCntr", {}),
  }),
  percentageCntr: (props) => ({
    display: "grid",
    gridGap: jssVariables.normal_gap,
    gridTemplateColumns: "repeat(2,max-content)",
    justifyContent: "space-between",
    ..._.get(props, "customStyles.percentageCntr", {}),
  }),
  divider: (props) => ({
    width: "100%",
    height: "1px",
    margin: 0,
    background: jssVariables.colors.text.dark,
    ..._.get(props, "customStyles.divider", {}),
  }),
  totalTitle: (props) => ({
    fontWeight: "400",
    ..._.get(props, "customStyles.totalTitle", {}),
  }),
  totalPrice: (props) => ({
    justifySelf: "end",
    fontWeight: "500",
    fontSize: "larger",
    ..._.get(props, "customStyles.totalPrice", {}),
  }),
  amountDueCntr: (props) => ({
    display: "grid",
    placeItems: "end",
    width: "100%",
    padding: [jssVariables.normal_gap, "4vw"],
    borderBottomLeftRadius: jssVariables.border_radius,
    borderBottomRightRadius: jssVariables.border_radius,
    background: tinycolor(jssVariables.colors.base_color)
      .lighten(40)
      .toString(),
    color: jssVariables.colors.base_color,
    fontSize: "large",
    fontWeight: "500",
    ..._.get(props, "customStyles.amountDueCntr", {}),
  }),
  iconAndText: (props) => ({
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: "max-content 1fr",
    gridGap: jssVariables.normal_gap,
    position: "relative",
    ..._.get(props, "customStyles.iconCntr", {}),
  }),
  iconCntr: (props) => ({
    display: "grid",
    placeItems: "center",
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    background: "white",
    border: `2px solid ${jssVariables.colors.base_color}`,
    color: jssVariables.colors.base_color,
    position: "relative",
    "&:after": {
      content: `''`,
      top: `100%`,
      left: `50%`,
      transform: "translateX(-50%)",
      position: "absolute",
      height: "200%",
      borderLeft: `3px solid ${jssVariables.colors.base_color}`,
    },
    ..._.get(props, "customStyles.iconCntr", {}),
  }),
  lastIconCntr: (props) => ({
    display: "grid",
    placeItems: "center",
    width: "2rem",
    height: "2rem",
    borderRadius: "50%",
    background: "white",
    border: `2px solid ${jssVariables.colors.base_color}`,
    color: jssVariables.colors.base_color,
    position: "relative",
    ..._.get(props, "customStyles.iconCntr", {}),
  }),
  icon: (props) => ({
    fontSize: "large",
    ..._.get(props, "customStyles.icon", {}),
  }),
  room: {
    borderRadius: "5px",
    border: `2px solid ${jssVariables.colors.base_color}`,
    margin: `0 calc(${jssVariables.normal_gap} * 3)`,
    padding: `${jssVariables.half_gap}!important`,
  },
  roomGuestList: {
    marginBottom: "0px",
  },
  [`@media ${jssVariables.media.smallscreen}`]: {
    content: (props) => ({
      width: "40rem",
      paddingTop: jssVariables.double_gap,
      "& > *:not(:last-child)": {
        padding: [
          0,
          `calc(${jssVariables.normal_gap} *3)`,
          0,
          `calc(${jssVariables.normal_gap} *3)`,
        ],
      },
      ..._.get(props, "customStyles.content.smallscreen", {}),
    }),
    btnsCntr: (props) => ({
      width: "40rem",
      justifySelf: "center",
      ..._.get(props, "customStyles.btnsCntr.smallscreen", {}),
    }),
    paymentTitle: (props) => ({
      fontSize: "x-large",
      ..._.get(props, "customStyles.paymentTitle.smallscreen", {}),
    }),
    amountDueCntr: (props) => ({
      padding: [jssVariables.half_gap, `calc(${jssVariables.normal_gap} *3)`],
      ..._.get(props, "customStyles.amountDueCntr.smallscreen", {}),
    }),
  },
  [`@media ${jssVariables.media.bigscreen}`]: {
    Deposit: (props) => ({
      ..._.get(props, "customStyles.Deposit.bigscreen", {}),
    }),
  },
});
const Deposit = ({ reference }) => {
  const { type, brand, cStyler } = useContext(StyleTypeContext);
  const { t } = useContext(TranslationContext);
  const history = useHistory();
  const dispatch = useDispatch();

  const priceDecimals = useSelector((state) => getPriceDecimalsSelector(state));

  const classes = styles({
    customStyles: cStyler({ type, brand, objName: "paymentDeposit" }),
  });

  const { price_info, adults, children, children_ages } = useSelector(
    (state) => ({
      price_info: state.TourPriceInfo?.price_info ?? {},
      adults: state.TourPriceInfo?.search_data?.adults ?? 0,
      children: state.TourPriceInfo?.search_data?.children ?? 0,
      children_ages: state.TourPriceInfo?.search_data?.children_ages ?? [],
    })
  );
  const group_uid = price_info?.price?.[0]?.group_uid;

  var txt = `${adults} ${adults > 1 ? "Adults" : "Adult"}`;

  if (children) {
    txt += ` ${children} ${children > 1 ? "Children" : "Child"} ${[
      ...new Set(children_ages),
    ].join(",")} years old.`;
  }

  const cxls = (price_info?.cancellation ?? []).filter(
    (cxl) => cxl.group_uid === group_uid
  );

  const totalPrice = price_info?.price?.[0]?.value ?? 0;
  const depositAmount = price_info?.price?.[0]?.deposit_amount ?? 0;
  const balanceAmount = price_info?.price?.[0]?.balance_amount ?? 0;
  const mainCurrency = price_info?.price?.[0]?.currency ?? "";
  const balanceDue = price_info?.price?.[0]?.balance_due;

  const description =
    depositAmount.toLocaleString("en-US", {
      style: "currency",
      currency: mainCurrency,
      maximumFractionDigits: priceDecimals,
    }) +
    " of the total amount will need to be paid upfront and the remaining amount on " +
    DateTime.fromISO(balanceDue).toLocaleString();

  const roomMapping = useSelector((state) => getRoomMappingInfoSelector(state));

  const onGoToGuests = useCallback(async () => {
    await dispatch(initGuestInformation(adults, children));
    await dispatch(gotoGuestInfo({ initialize: false }));
  }, [dispatch, adults, children]);

  const onGoBack = useCallback(() => {
    history.push(`/tour/MI_${reference}`);
    dispatch(resetPriceInfo());
  }, [dispatch, reference, history]);

  return (
    <div className={classes.Deposit}>
      <div className={classes.content}>
        <div className={classes.paymentTitle}>{t("Payment")}</div>
        {depositAmount > 0 && (
          <div className={classes.paymentDescription}>{description}</div>
        )}
        <div className={classes.detailsCntr}>
          <div className={classes.iconAndText}>
            <span className={classes.iconCntr}>
              <InlineIcon
                icon="ic:baseline-location-on"
                className={classes.icon}
              />
            </span>
            <span className={classes.titleCntr}>
              {price_info.title} {/* - {price_info.product_version} */}
            </span>
          </div>
          <div className={classes.iconAndText}>
            <span className={classes.iconCntr}>
              <InlineIcon icon="ic:baseline-person" className={classes.icon} />
            </span>
            <span className={classes.paxCntr}>{txt}</span>
          </div>
          <div className={classes.iconAndText}>
            <span className={classes.iconCntr}>
              <InlineIcon
                icon="ic:baseline-calendar-month"
                className={classes.icon}
              />
            </span>
            <span className={classes.dateCntr}>
              {t("on")}{" "}
              {DateTime.fromISO(price_info.operation_date).toLocaleString()}
            </span>
          </div>
          <div className={classes.iconAndText}>
            <span
              className={
                classes.lastIconCntr
              } /* className={classes.iconCntr} */
            >
              <InlineIcon icon="carbon:money" className={classes.icon} />
            </span>
            <span className={classes.detailsTotalPrice}>
              {totalPrice.toLocaleString("en-US", {
                style: "currency",
                currency: mainCurrency,
                maximumFractionDigits: priceDecimals,
              })}
            </span>
          </div>
          {/* <div className={classes.iconAndText}> */}
          {/*   <span className={classes.lastIconCntr}> */}
          {/*     <InlineIcon */}
          {/*       icon="tabler:shield-cancel" */}
          {/*       className={classes.icon} */}
          {/*     /> */}
          {/*   </span> */}
          {/*   <span className={classes.dateCntr}> */}
          {/*     {cxls.map((cxl, idx) => { */}
          {/*       const from = DateTime.fromISO(cxl.from); */}
          {/*       const to = DateTime.fromISO(cxl.to); */}

          {/*       return ( */}
          {/*         <div className={classes.normalText} key={idx}> */}
          {/*           {t("from")} <strong>{from.toLocaleString()}</strong>{" "} */}
          {/*           {t("to")} <strong>{to.toLocaleString()}</strong>{" "} */}
          {/*           {t("cancellationIs")}{" "} */}
          {/*           <strong>{_.startCase(cxl.type)}</strong> */}
          {/*         </div> */}
          {/*       ); */}
          {/*     })}{" "} */}
          {/*   </span> */}
          {/* </div> */}
        </div>
        {Object.entries(roomMapping).map(([__, guestRoomMappings], idx) => {
          // guestRoomMappings: ["<uuid>___<uuid>___ADT___1","Single"]
          const adtNo = guestRoomMappings.filter(([guestCode, __]) =>
            guestCode.includes("ADT")
          ).length;
          const chdNo = guestRoomMappings.filter(([guestCode, __]) =>
            guestCode.includes("CHD")
          ).length;
          const [___, roomName] = guestRoomMappings[0];

          return (
            <div key={idx} className={classes.room}>
              <span>
                <strong>Room {idx + 1}:</strong> {roomName}
              </span>
              <ul className={classes.roomGuestList}>
                <li key={idx}>Adults: {adtNo}</li>
                {chdNo > 0 && <li key={idx}>Children: {chdNo}</li>}
              </ul>
            </div>
          );
        })}
        {depositAmount > 0 && (
          <div className={classes.pricesCntr}>
            <div className={classes.percentageCntr}>
              <div className={classes.depositPercentage}>
                {t("Deposit (due now)")}
              </div>
              <div className={classes.depositPrice}>
                {depositAmount.toLocaleString("en-US", {
                  style: "currency",
                  currency: mainCurrency,
                  maximumFractionDigits: priceDecimals,
                })}
              </div>
              <div className={classes.balancePercentage}>
                Balance (due by{" "}
                {DateTime.fromISO(balanceDue).toLocaleString(
                  DateTime.DATE_MED_WITH_WEEKDAY
                )}
                )
              </div>
              <div className={classes.balancePrice}>
                {balanceAmount.toLocaleString("en-US", {
                  style: "currency",
                  currency: mainCurrency,
                  maximumFractionDigits: priceDecimals,
                })}
              </div>
            </div>
            <div></div>
            <hr className={classes.divider} />
            <div className={classes.totalPrice}>
              <span className={classes.totalTitle}>{t("Total")}: </span>
              {totalPrice.toLocaleString("en-US", {
                style: "currency",
                currency: mainCurrency,
                maximumFractionDigits: priceDecimals,
              })}
            </div>
          </div>
        )}
        <div className={classes.amountDueCntr}>
          <span className={classes.amountDue}>
            {t("Amount Due Now")}:{" "}
            {(depositAmount > 0 ? depositAmount : totalPrice).toLocaleString(
              "en-US",
              {
                style: "currency",
                currency: mainCurrency,
                maximumFractionDigits: priceDecimals,
              }
            )}
          </span>
        </div>
      </div>
      <div className={classes.btnsCntr}>
        <Button text={t("Back")} onClick={onGoBack} />
        <Button text={t("Proceed")} onClick={onGoToGuests} />
      </div>
    </div>
  );
};
Deposit.defaultProps = {
  reference: "",
};
Deposit.propTypes = {
  reference: PropTypes.string.isRequired,
};
export default Deposit;
